import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode, type SyntheticEvent } from 'react';

import { ApiError, getKycDetails, getUserProfile, shipCard } from '../api/client.ts';
import { IconChevronLeft } from '../components/icons.tsx';
import {
  ShippingFields,
  allShippingFields,
  blankShipping,
  buildShippingPayload,
  validateShipping,
  type ShippingFieldErrors,
  type ShippingFormState,
} from '../components/shipping-fields.tsx';
import { showToast } from '../components/toast.tsx';

import { rootRoute } from './__root.tsx';

export interface ShipSearch {
  cardId?: string;
}

const ShipPage = (): ReactNode => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { cardId } = useSearch({ from: shipRoute.id });

  const [shipping, setShipping] = useState<ShippingFormState>(blankShipping);
  const [shippingTouched, setShippingTouched] = useState<
    Partial<Record<keyof ShippingFieldErrors, boolean>>
  >({});
  const [kycPrefilled, setKycPrefilled] = useState(false);
  const [profilePrefilled, setProfilePrefilled] = useState(false);

  useEffect(() => {
    if (cardId === undefined) void navigate({ to: '/' });
  }, [cardId, navigate]);

  const kycAddressQuery = useQuery({
    queryKey: ['kycAddress'],
    queryFn: ({ signal }) => getKycDetails(signal),
    staleTime: 5 * 60 * 1000,
  });

  const userProfileQuery = useQuery({
    queryKey: ['userProfile'],
    queryFn: ({ signal }) => getUserProfile(signal),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (kycPrefilled || kycAddressQuery.data === undefined) return;
    const a = kycAddressQuery.data;
    setShipping((prev) => ({
      ...prev,
      firstName: a.firstName ?? prev.firstName,
      lastName: a.lastName ?? prev.lastName,
      line1: a.residentialAddressLine1 ?? prev.line1,
      line2: a.residentialAddressLine2 ?? prev.line2,
      city: a.residentialAddressCity ?? prev.city,
      postalCode: a.residentialAddressPostalCode ?? prev.postalCode,
      country: a.residentialAddressCountry ?? prev.country,
    }));
    setKycPrefilled(true);
  }, [kycAddressQuery.data, kycPrefilled]);

  useEffect(() => {
    if (profilePrefilled || userProfileQuery.data === undefined) return;
    // Email only — the recipient phone is NOT prefilled from the profile here.
    // The profile carries the OTP phone, not the phone entered when the card
    // was created, so prefilling it would surface the wrong number. The user
    // enters the recipient phone explicitly on this standalone shipping form.
    setShipping((prev) => ({ ...prev, email: userProfileQuery.data?.email ?? prev.email }));
    setProfilePrefilled(true);
  }, [userProfileQuery.data, profilePrefilled]);

  const shippingErrors = validateShipping(shipping);
  const hasErrors = Object.keys(shippingErrors).length > 0;

  const ship = useMutation({
    mutationFn: (): Promise<{ readonly ok: true }> => {
      if (cardId === undefined) return Promise.reject(new Error('No card selected'));
      return shipCard(cardId, buildShippingPayload(shipping));
    },
    onSuccess: (): void => {
      void qc.invalidateQueries({ queryKey: ['cards'] });
      showToast('Shipping arranged successfully.');
      void navigate({ to: '/', search: cardId !== undefined ? { cardId } : {} });
    },
    onError: (err: unknown): void => {
      const msg =
        err instanceof ApiError && err.message
          ? err.message
          : 'Could not arrange shipping. Try again.';
      showToast(msg);
    },
  });

  const showError = (field: keyof ShippingFieldErrors): string | undefined =>
    shippingTouched[field] === true || ship.isError ? shippingErrors[field] : undefined;

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setShippingTouched(() => {
      const all: Partial<Record<keyof ShippingFieldErrors, boolean>> = {};
      for (const f of allShippingFields) all[f] = true;
      return all;
    });
    if (hasErrors || ship.isPending || cardId === undefined) return;
    ship.mutate();
  };

  return (
    <div className="screen">
      <Topbar />
      <main className="new-card-main">
        <form className="new-card-form" onSubmit={handleSubmit} noValidate>
          <div className="ship-fields">
            <ShippingFields
              shipping={shipping}
              onPatch={(patch): void => {
                setShipping((s) => ({ ...s, ...patch }));
              }}
              onTouch={(...fields): void => {
                setShippingTouched((t) => {
                  const next = { ...t };
                  for (const f of fields) next[f] = true;
                  return next;
                });
              }}
              showError={showError}
            />
          </div>

          <button type="submit" className="new-card-submit" disabled={ship.isPending}>
            {ship.isPending ? 'Submitting…' : 'Arrange shipping'}
          </button>
        </form>
      </main>
    </div>
  );
};

const Topbar = (): ReactNode => {
  const navigate = useNavigate();
  return (
    <header className="topbar">
      <button
        type="button"
        className="topbar-back"
        aria-label="Back"
        onClick={(): void => {
          void navigate({ to: '/' });
        }}
      >
        <IconChevronLeft />
      </button>
      <h1 className="topbar-title">Ship card</h1>
      <span className="topbar-spacer" aria-hidden="true" />
    </header>
  );
};

export const shipRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ship',
  component: ShipPage,
  validateSearch: (s: Record<string, unknown>): ShipSearch =>
    typeof s.cardId === 'string' ? { cardId: s.cardId } : {},
});
